import React, { useState } from "react";
import { tapHalfLead } from "./tapTarget";

/**
 * RoleStrip — issue #750 § 5.8. Pill chips (a role/format picker), with an
 * optional group header and right-hand count — used for things like the
 * player/coach ticks on sign-up and the squad's role grouping.
 *
 * `gap` (issue #767, S5) — `Squad.dc.html:93` draws this strip's own chip
 * gap at 6px, not this component's original 8px default (no prior caller
 * used this component at all — see `kit/index.js`'s barrel — so 8px had no
 * drawn instance backing it either way). Kept as a prop, defaulting to the
 * original 8, rather than hard-changed to 6: a future caller may yet draw
 * 8, and this is the same "genuinely different numbers get their own
 * token" rule `kit/ListRow.jsx`'s own header names.
 *
 * `tapBox` (#1394, D221 G4, D354) — opt-in, off by default so a caller that
 * does not ask draws exactly what it always drew. The drawn pill is 37.5px
 * tall at the 13px size; on, the button is a transparent 44px box and the
 * pill is drawn inside it. `marginBlock` pulls the margin box back to the
 * pill's own height (one line plus the pill's 16px padding and 2px border),
 * so rows keep their pitch and the hit area grows into the 6.5px around
 * the pill. Only the box changes: the pill is the same size and the same
 * place. The button's own outline is dropped (it would trace the invisible
 * 44px box); a keyboard-focused chip gets the kit's 2px navy ring on the
 * pill instead (#1334's `.nw-field` ring, WCAG 2.4.7).
 */
export function RoleStrip({
  groupLabel = null,
  count = null,
  options,
  value,
  onChange,
  gap = 8,
  tapBox = false,
  className = "",
  "data-testid": testId,
  ...rest
}) {
  // The value of the chip that has KEYBOARD focus (`:focus-visible`), tapBox only.
  const [ringed, setRinged] = useState(null);
  const keyboardFocus = (el) => {
    try {
      return el.matches(":focus-visible");
    } catch {
      return true;
    }
  };
  return (
    <div data-testid={testId} className={className} {...rest}>
      {groupLabel && (
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          {/* `Squad.dc.html:119` and `GroundsNearYou.dc.html:87` both draw
              this at 11px, not the 10px `.nw-section` sets — #750 § 5.8
              names 11px explicitly too. */}
          <span className="nw-section" style={{ fontSize: 11 }}>
            {groupLabel}
          </span>
          {count != null && <span className="nw-caption-strong" style={{ color: "var(--nw-navy)" }}>{count}</span>}
        </div>
      )}
      <div className="flex flex-wrap" style={{ gap }}>
        {options.map((opt) => {
          const selected = Array.isArray(value) ? value.includes(opt.value) : opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={opt["data-testid"]}
              onClick={() => onChange?.(opt.value)}
              aria-pressed={selected}
              onFocus={tapBox ? (e) => setRinged(keyboardFocus(e.currentTarget) ? opt.value : null) : undefined}
              onBlur={tapBox ? () => setRinged(null) : undefined}
              style={
                tapBox
                  ? {
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "var(--nw-size-tap)",
                      minWidth: "var(--nw-size-tap)",
                      flexShrink: 0,
                      marginBlock: `calc(${tapHalfLead()} + 9px)`,
                      padding: 0,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                      fontSize: 13,
                      cursor: "pointer",
                    }
                  : {
                      borderRadius: "var(--nw-radius-pill)",
                      padding: "8px 14px",
                      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                      fontSize: 13,
                      fontWeight: selected ? 700 : 600,
                      background: selected ? "var(--nw-navy)" : "var(--nw-surface)",
                      color: selected ? "#FFFFFF" : "var(--nw-ink)",
                      border: selected ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line)",
                      cursor: "pointer",
                    }
              }
            >
              {tapBox ? (
                <span
                  data-role-pill=""
                  style={{
                    display: "block",
                    borderRadius: "var(--nw-radius-pill)",
                    padding: "8px 14px",
                    fontWeight: selected ? 700 : 600,
                    background: selected ? "var(--nw-navy)" : "var(--nw-surface)",
                    color: selected ? "#FFFFFF" : "var(--nw-ink)",
                    border: selected ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line)",
                    boxShadow: ringed === opt.value ? "0 0 0 2px var(--nw-navy)" : undefined,
                  }}
                >
                  {opt.label}
                </span>
              ) : (
                opt.label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default RoleStrip;
